import React, { useRef, useState } from "react";
import { Mic, Square, Upload, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";
import { getMicStream, micErrorMessage } from "@/lib/mic";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

export function RecordingOverlay({ mode, onStop, lang }) {
  // mode: "recording" | "translating"
  const recording = mode === "recording";
  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-primary/95 backdrop-blur-md animate-pop" data-testid="recording-overlay">
      <div className="flex flex-col items-center text-white px-6 text-center">
        <div className="relative grid place-items-center mb-10">
          {recording && (
            <>
              <span className="absolute h-32 w-32 rounded-full bg-accent/40 mic-ring" />
              <span className="absolute h-32 w-32 rounded-full bg-accent/30 mic-ring" style={{ animationDelay: "0.6s" }} />
              <span className="absolute h-32 w-32 rounded-full bg-accent/20 mic-ring" style={{ animationDelay: "1.2s" }} />
            </>
          )}
          <div className={`relative h-32 w-32 rounded-full grid place-items-center shadow-2xl ${recording ? "bg-accent mic-bob" : "bg-white/15"}`}>
            {recording ? <Mic size={52} className="text-white" /> : <Loader2 size={52} className="text-white animate-spin" />}
          </div>
        </div>

        {recording ? (
          <>
            <div className="flex items-end gap-1.5 h-10 mb-5">
              {[0, 1, 2, 3, 4, 5, 6].map((i) => (
                <span key={i} className="wave-bar w-1.5 h-full bg-accent rounded-full" style={{ animationDelay: `${i * 0.12}s` }} />
              ))}
            </div>
            <h2 className="font-head font-bold text-2xl sm:text-3xl">{lang === "ar" ? "جارٍ الاستماع…" : "Listening…"}</h2>
            <p className="text-white/70 mt-2 max-w-xs">{lang === "ar" ? "تحدّث الآن، ثم اضغط إيقاف للترجمة" : "Speak now, then tap Stop to translate"}</p>
            <button
              onClick={onStop}
              data-testid="overlay-stop-btn"
              className="mt-9 flex items-center gap-2 h-14 px-8 rounded-full bg-white text-primary font-semibold text-lg shadow-lg hover:scale-105 transition-transform"
            >
              <Square size={20} className="fill-current" /> {lang === "ar" ? "إيقاف" : "Stop"}
            </button>
          </>
        ) : (
          <>
            <h2 className="font-head font-bold text-2xl sm:text-3xl">{lang === "ar" ? "جارٍ الترجمة…" : "Translating…"}</h2>
            <p className="text-white/70 mt-2 max-w-xs">{lang === "ar" ? "يتم تحويل الصوت إلى نص وترجمته" : "Transcribing your audio and translating it"}</p>
          </>
        )}
      </div>
    </div>
  );
}

export function VoiceRecorder({ targetLang, onResult, busy, setBusy }) {
  const { t, lang } = useI18n();
  const [recording, setRecording] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const fileRef = useRef(null);

  const sendAudio = async (blob, filename) => {
    setBusy(true);
    const form = new FormData();
    form.append("file", blob, filename);
    form.append("target_lang", targetLang);
    try {
      const { data } = await api.post("/voice/translate", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Voice translation failed");
    } finally {
      setBusy(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await getMicStream();
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((tk) => tk.stop());
        sendAudio(blob, "note.webm");
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (err) {
      toast.error(micErrorMessage(err, lang));
    }
  };

  const stopRecording = () => {
    mediaRef.current?.stop();
    setRecording(false);
  };

  return (
    <>
      {(recording || busy) && (
        <RecordingOverlay mode={recording ? "recording" : "translating"} onStop={stopRecording} lang={lang} />
      )}
      <div className="flex items-center gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="audio/*"
          className="hidden"
          data-testid="voice-file-input"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) sendAudio(f, f.name);
            e.target.value = "";
          }}
        />
        <button
          onClick={startRecording}
          disabled={busy || recording}
          data-testid="voice-record-btn"
          className="flex items-center gap-2 h-11 px-4 rounded-full bg-primary text-white font-medium hover:bg-primary/90 transition-colors disabled:opacity-50"
        >
          <Mic size={17} /> {t("record")}
        </button>
        <button
          onClick={() => fileRef.current?.click()}
          disabled={busy || recording}
          data-testid="voice-upload-btn"
          className="flex items-center gap-2 h-11 px-4 rounded-full border border-border font-medium hover:bg-secondary transition-colors disabled:opacity-50"
        >
          <Upload size={17} /> {t("upload")}
        </button>
      </div>
    </>
  );
}
